/**
 * TealButton — Botão primário padronizado
 *
 * Usa os tokens do design system para tamanhos e gradientes.
 * Substitui o PrimaryButton existente com suporte a tamanhos semânticos.
 *
 * Tamanhos:
 *   xs     — 28px — chips clicáveis, filtros
 *   sm     — 32px — header mobile compacto
 *   md     — 36px — header desktop
 *   lg     — 48px — formulários
 *   mobile — 52px — ação principal mobile
 *
 * Uso:
 *   <TealButton size="md" onClick={handleAdd} startIcon={<Add />}>Nova Playlist</TealButton>
 *   <TealButton size="mobile" fullWidth loading={isLoading}>Entrar</TealButton>
 */

import { LoadingButton, LoadingButtonProps } from '@mui/lab'
import { useTheme } from '@mui/material'
import { ds } from '../../theme/ds'

type TealSize = 'xs' | 'sm' | 'md' | 'lg' | 'mobile'

type Props = Omit<LoadingButtonProps, 'size'> & {
  size?: TealSize
}

const sizeMap: Record<TealSize, { height: number; fontSize: number; px: number; borderRadius: number }> = {
  xs:     { height: ds.height.btnXs,     fontSize: 11, px: 1,   borderRadius: ds.radius.sm },
  sm:     { height: ds.height.btnSm,     fontSize: 12, px: 1.5, borderRadius: ds.radius.sm },
  md:     { height: ds.height.btnMd,     fontSize: 13, px: 2,   borderRadius: ds.radius.sm },
  lg:     { height: ds.height.btnLg,     fontSize: 15, px: 2.5, borderRadius: ds.radius.sm },
  mobile: { height: ds.height.btnMobile, fontSize: 15, px: 2.5, borderRadius: ds.radius.sm },
}

export default function TealButton({ size = 'md', sx, children, ...props }: Props) {
  const isDark = useTheme().palette.mode === 'dark'
  const s = sizeMap[size]

  return (
    <LoadingButton
      variant="contained"
      {...props}
      sx={{
        height: s.height,
        fontSize: s.fontSize,
        px: s.px,
        fontWeight: ds.font.semibold,
        letterSpacing: '0.2px',
        borderRadius: `${s.borderRadius}px`,
        background: ds.gradient.teal,
        color: '#fff',
        boxShadow: 'none',
        transition: `all ${ds.motion.normal}`,
        '&:hover': {
          background: ds.gradient.tealHover,
          boxShadow: ds.shadow.fab,
          transform: 'translateY(-1px)',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
        '&.MuiLoadingButton-loading': {
          background: isDark ? 'rgba(20,184,160,0.18)' : 'rgba(20,184,160,0.12)',
        },
        ...sx,
      }}
    >
      {children}
    </LoadingButton>
  )
}
